// React / Next
import { useEffect, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

// Components
import { Typography } from '@archieai/ui';
import { ValuePropositionBlock } from '../../components/ValuePropositionBlock/ValuePropositionBlock';
import { LetsTalkSection } from '../../components/LetsTalkSection/LetsTalkSection';
import { DigitalRainVector } from '../../components/magicVectors/DigitalRainVector/DigitalRainVector';
import { BlindsVector } from '../../components/magicVectors/BlindsVector/BlindsVector';
import { MysticMirrorVector } from '../../components/magicVectors/MysticMirrorVector/MysticMirrorVector';
import { DiagonalSpinVector } from '../../components/magicVectors/DiagonalSpinVector/DiagonalSpinVector';
import { SEO } from '../../components/SEO/SEO';

// Hooks
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Types
import type { LandingPage as LandingPageType, VectorType } from '../../lib/sanity/types';

type LandingPageProps = {
  landing: LandingPageType;
};

/** Maps a vectorType string from CMS to the corresponding animated vector component */
const VECTOR_MAP: Record<VectorType, (className: string) => ReactNode> = {
  digitalRain: (className) => <DigitalRainVector className={className} />,
  blinds: (className) => <BlindsVector className={className} />,
  mysticMirror: (className) => <MysticMirrorVector className={className} />,
  diagonalSpin: (className) => <DiagonalSpinVector className={className} />,
};

const getVector = (vectorType: VectorType): ReactNode => {
  const factory = VECTOR_MAP[vectorType] ?? VECTOR_MAP.digitalRain;
  return factory('w-full h-full');
};

/**
 * LandingPage - Dynamic landing page driven by Sanity CMS content.
 * Renders hero, value propositions with animated vectors, and a closing CTA.
 */
export const LandingPage = ({ landing }: LandingPageProps) => {
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent('landing_page_viewed', {
      slug: landing.slug,
      title: landing.title,
    });
  }, [landing.slug, landing.title, trackEvent]);

  return (
    <div className="flex flex-col items-center w-full bg-white">
      <SEO
        title={landing.seo?.metaTitle || landing.title}
        description={landing.seo?.metaDescription || landing.heroDescription}
        keywords={landing.seo?.keywords}
        canonicalUrl={landing.seo?.canonicalUrl || `/landing-pages/${landing.slug}`}
      />

      {/* Hero Section */}
      <section className="flex flex-col items-center gap-[64px] px-[24px] py-[80px] w-full max-w-[1016px] max-md:py-[48px] max-md:gap-[48px]">
        {/* Audience Tag */}
        <div className="flex items-center justify-center">
          <Typography
            variant="h4"
            className="text-orange-1000 text-center"
          >
            {landing.audienceTag}
          </Typography>
        </div>

        {/* Hero Content */}
        <div className="flex flex-col items-center gap-[24px] text-center">
          <Typography
            as="h1"
            variant="display"
            className="text-blue-1000 max-w-[878px]"
          >
            {landing.heroTitle}
          </Typography>
          <Typography
            as="p"
            variant="h3"
            className="text-blue-1000 max-w-[878px]"
          >
            {landing.heroDescription}
          </Typography>
        </div>

        {/* CTA Button */}
        {landing.ctaText && (() => {
          const href = landing.ctaLink || '/contact-sales';
          const isExternal = href.startsWith('http');
          const buttonEl = (
            <button className="inline-flex items-center justify-center gap-[var(--spacing-8)] px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-[var(--radius-8)] font-body text-[16px] leading-[24px] font-normal transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-orange-800 text-white hover:bg-orange-1000 focus:ring-orange-800">
              {landing.ctaText}
            </button>
          );
          return isExternal ? (
            <a href={href} target="_blank" rel="noopener noreferrer">{buttonEl}</a>
          ) : (
            <Link to={href}>{buttonEl}</Link>
          );
        })()}
      </section>

      {/* Value Propositions */}
      {landing.valuePropositions?.map((vp) => (
        <section
          key={vp._key}
          className="flex justify-center px-[24px] py-[32px] w-full max-md:py-[24px]"
        >
          <ValuePropositionBlock
            title={vp.title}
            description={vp.description}
            vector={getVector(vp.vectorType)}
            textPosition={vp.textPosition}
          />
        </section>
      ))}

      {/* Let's Talk CTA Section */}
      <LetsTalkSection
        title={landing.letsTalkTitle ?? undefined}
        description={landing.letsTalkDescription ?? undefined}
        ctaText={landing.letsTalkCtaText ?? undefined}
        ctaLink={landing.letsTalkCtaLink ?? undefined}
      />
    </div>
  );
};
